import { usePermissionCheck } from './usePermissionCheck';
import { PERMISSIONS } from '../../config/permissions';
import { toast } from 'react-toastify';

export const useOfferPermissions = () => {
  const { canPerform, isMainVendor, isAuthenticated } = usePermissionCheck();

  // Individual permission checks
  const canViewDeals = canPerform(PERMISSIONS.MODULES.DEALS_MANAGEMENT, PERMISSIONS.ACTIONS.VIEW_DEALS) || isMainVendor;
  const canCreateOffers = canPerform(PERMISSIONS.MODULES.DEALS_MANAGEMENT, PERMISSIONS.ACTIONS.CREATE_OFFER) || isMainVendor;
  const canEditOffers = canPerform(PERMISSIONS.MODULES.DEALS_MANAGEMENT, PERMISSIONS.ACTIONS.EDIT_OFFER) || isMainVendor;
  const canDeleteOffers = canPerform(PERMISSIONS.MODULES.DEALS_MANAGEMENT, PERMISSIONS.ACTIONS.DELETE_OFFER) || isMainVendor;
  const canBlastDeals = canPerform(PERMISSIONS.MODULES.DEALS_MANAGEMENT, PERMISSIONS.ACTIONS.BLAST_DEAL) || isMainVendor;

  // Combined permission checks
  const hasAnyOfferAccess = canViewDeals || canCreateOffers || canEditOffers || canDeleteOffers || canBlastDeals;
  const hasFullOfferAccess = canViewDeals && canCreateOffers && canEditOffers && canDeleteOffers && canBlastDeals;

  // Permission enforcement functions
  const enforceViewPermission = () => {
    if (!canViewDeals) {
      toast.error("You don't have permission to view deals");
      return false;
    }
    return true;
  };

  const enforceCreatePermission = () => {
    if (!canCreateOffers) {
      toast.error("You don't have permission to create offers");
      return false;
    }
    return true;
  };

  const enforceEditPermission = () => {
    if (!canEditOffers) {
      toast.error("You don't have permission to edit offers");
      return false;
    }
    return true;
  };

  const enforceDeletePermission = () => {
    if (!canDeleteOffers) {
      toast.error("You don't have permission to delete offers");
      return false;
    }
    return true;
  };

  const enforceBlastPermission = () => {
    if (!canBlastDeals) {
      toast.error("You don't have permission to blast deals");
      return false;
    }
    return true;
  };

  // Permission metadata for UI components
  const getPermissionInfo = (action) => {
    const permissionMap = {
      view: {
        hasPermission: canViewDeals,
        module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
        action: PERMISSIONS.ACTIONS.VIEW_DEALS,
        description: "View Deals in Deals Management",
        errorMessage: "You don't have permission to view deals"
      },
      create: {
        hasPermission: canCreateOffers,
        module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
        action: PERMISSIONS.ACTIONS.CREATE_OFFER,
        description: "Create Offer in Deals Management",
        errorMessage: "You don't have permission to create offers"
      },
      edit: {
        hasPermission: canEditOffers,
        module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
        action: PERMISSIONS.ACTIONS.EDIT_OFFER,
        description: "Edit Offer in Deals Management",
        errorMessage: "You don't have permission to edit offers"
      },
      delete: {
        hasPermission: canDeleteOffers,
        module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
        action: PERMISSIONS.ACTIONS.DELETE_OFFER,
        description: "Delete Offer in Deals Management",
        errorMessage: "You don't have permission to delete offers"
      },
      blast: {
        hasPermission: canBlastDeals,
        module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
        action: PERMISSIONS.ACTIONS.BLAST_DEAL,
        description: "Blast Deal in Deals Management",
        errorMessage: "You don't have permission to blast deals"
      }
    };

    return permissionMap[action] || null;
  };

  // Check if user has specific permission with optional enforcement
  const checkPermission = (action, enforce = false) => {
    const info = getPermissionInfo(action);
    if (!info) return false;

    if (enforce && !info.hasPermission) {
      toast.error(info.errorMessage);
      return false;
    }

    return info.hasPermission;
  };

  return {
    // Individual permissions
    canViewDeals,
    canCreateOffers,
    canEditOffers,
    canDeleteOffers,
    canBlastDeals,
    
    // Combined permissions
    hasAnyOfferAccess,
    hasFullOfferAccess,
    
    // Authentication status
    isAuthenticated,
    isMainVendor,
    
    // Enforcement functions
    enforceViewPermission,
    enforceCreatePermission,
    enforceEditPermission,
    enforceDeletePermission,
    enforceBlastPermission,
    
    // Utility functions
    getPermissionInfo,
    checkPermission,
    
    // Permission objects for components
    viewPermission: {
      module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      action: PERMISSIONS.ACTIONS.VIEW_DEALS
    },
    createPermission: {
      module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      action: PERMISSIONS.ACTIONS.CREATE_OFFER
    },
    editPermission: {
      module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      action: PERMISSIONS.ACTIONS.EDIT_OFFER
    },
    deletePermission: {
      module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      action: PERMISSIONS.ACTIONS.DELETE_OFFER
    },
    blastPermission: {
      module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
      action: PERMISSIONS.ACTIONS.BLAST_DEAL
    }
  };
}; 